import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Loader2, CheckCircle2, CalendarDays, Clock } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { SERVICES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (d) =>
  d ? d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }) : "";

const toKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const genSlots = (start, end) => {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  const res = [];
  let cur = sh * 60 + sm;
  const last = eh * 60 + em;
  while (cur <= last) {
    res.push(`${String(Math.floor(cur / 60)).padStart(2, "0")}:${String(cur % 60).padStart(2, "0")}`);
    cur += 30;
  }
  return res;
};

// Mo–Fr: Besichtigung ab 16:30 · Sa: ganztags 8:00–20:00 · So: geschlossen
const slotsForDate = (d) => {
  if (!d) return [];
  const dow = d.getDay();
  if (dow === 6) return genSlots("08:00", "19:30");
  if (dow >= 1 && dow <= 5) return genSlots("16:30", "19:30");
  return [];
};

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [booked, setBooked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const fetchBooked = async (d) => {
    try {
      const res = await axios.get(`${API}/booked-slots`, { params: { date_key: toKey(d) } });
      setBooked(res.data.booked || []);
    } catch {
      setBooked([]);
    }
  };

  const onSelectDate = (d) => {
    setDate(d);
    setTime("");
    if (d) fetchBooked(d);
    else setBooked([]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Bitte Name, Telefon und E-Mail ausfüllen.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        preferred_date: date ? formatDate(date) : null,
        preferred_time: time || null,
        date_key: date ? toKey(date) : null,
      });
      setDone(true);
      toast.success("Anfrage gesendet! Sie erhalten eine Bestätigung per E-Mail.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
      setDate(null);
      setTime("");
      setBooked([]);
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error(err.response.data.detail || "Zeitfenster bereits vergeben.");
        setTime("");
        if (date) fetchBooked(date);
      } else {
        toast.error("Etwas ist schiefgelaufen. Bitte rufen Sie uns an.");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-white/40 focus:border-gold focus:outline-none transition-colors";

  const isDisabledDay = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today || d.getDay() === 0; // Sonntag deaktiviert
  };

  const slots = slotsForDate(date);

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        data-testid="contact-success"
        className="rounded-2xl border border-gold/30 bg-surface p-10 text-center"
      >
        <CheckCircle2 className="mx-auto text-gold mb-5" size={56} />
        <h3 className="font-serif text-3xl text-white mb-3">Vielen Dank!</h3>
        <p className="text-white/60 max-w-sm mx-auto">
          Ihre Anfrage für eine kostenlose Besichtigung ist eingegangen. Eine Bestätigung inkl. Kalender-Einladung wurde an Ihre E-Mail-Adresse gesendet – wir melden uns zeitnah zur Terminbestätigung.
        </p>
        <button
          onClick={() => setDone(false)}
          className="mt-6 text-sm text-gold hover:text-gold-light underline underline-offset-4"
        >
          Weitere Anfrage senden
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-7">
      <div className="grid sm:grid-cols-2 gap-7">
        <input data-testid="input-name" className={inputCls} placeholder="Ihr Name *" value={form.name} onChange={update("name")} />
        <input data-testid="input-phone" className={inputCls} placeholder="Telefon *" value={form.phone} onChange={update("phone")} />
      </div>
      <input data-testid="input-email" type="email" className={inputCls} placeholder="E-Mail *" value={form.email} onChange={update("email")} />

      <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
        <SelectTrigger
          data-testid="select-service"
          className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 h-auto text-white focus:ring-0 focus:border-gold data-[placeholder]:text-white/40"
        >
          <SelectValue placeholder="Gewünschte Leistung" />
        </SelectTrigger>
        <SelectContent className="bg-surface border-white/10 text-white">
          {SERVICES.map((s) => (
            <SelectItem key={s.slug} value={s.title} className="focus:bg-gold/20 focus:text-white">
              {s.title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Wunschtermin: Kalender + Uhrzeit */}
      <div className="grid sm:grid-cols-2 gap-7">
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="date-trigger"
              className={`flex items-center gap-3 w-full border-b border-white/20 py-3 text-left transition-colors hover:border-gold focus:border-gold focus:outline-none ${date ? "text-white" : "text-white/40"}`}
            >
              <CalendarDays size={18} className="text-gold shrink-0" />
              {date ? formatDate(date) : "Wunschtermin (Datum)"}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0 bg-surface border-white/10">
            <Calendar
              mode="single"
              selected={date}
              onSelect={onSelectDate}
              disabled={isDisabledDay}
              data-testid="calendar"
              classNames={{
                caption_label: "text-sm font-medium text-white",
                nav_button: "h-7 w-7 bg-transparent p-0 text-white/70 hover:text-gold border border-white/15 rounded-md",
                head_cell: "text-white/40 rounded-md w-9 font-normal text-[0.75rem]",
                day: "h-9 w-9 p-0 font-normal text-white/80 hover:bg-gold/15 hover:text-white rounded-md transition-colors",
                day_selected: "bg-gold text-ink hover:bg-gold-light hover:text-ink focus:bg-gold focus:text-ink",
                day_today: "border border-gold/50 text-gold",
                day_disabled: "text-white/20 opacity-50 hover:bg-transparent",
                day_outside: "text-white/20",
              }}
            />
          </PopoverContent>
        </Popover>

        <Select value={time} onValueChange={setTime} disabled={!date}>
          <SelectTrigger
            data-testid="select-time"
            className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 h-auto text-white focus:ring-0 focus:border-gold data-[placeholder]:text-white/40 disabled:opacity-50 [&>span]:flex [&>span]:items-center [&>span]:gap-3"
          >
            <Clock size={18} className="text-gold shrink-0" />
            <SelectValue placeholder={date ? "Uhrzeit wählen" : "Zuerst Datum wählen"} />
          </SelectTrigger>
          <SelectContent className="bg-surface border-white/10 text-white max-h-64">
            {slots.map((t) => {
              const isBooked = booked.includes(t);
              return (
                <SelectItem
                  key={t}
                  value={t}
                  disabled={isBooked}
                  className="focus:bg-gold/20 focus:text-white data-[disabled]:opacity-40"
                >
                  {t} Uhr{isBooked ? " — belegt" : ""}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
      <p className="text-xs text-white/35 -mt-4">
        Besichtigungstermine: Mo–Fr ab 16:30 Uhr · Sa 8:00–20:00 Uhr. Bereits vergebene Zeiten sind ausgegraut.
      </p>

      <textarea
        data-testid="input-message"
        rows={3}
        className={inputCls + " resize-none"}
        placeholder="Ihre Nachricht (optional)"
        value={form.message}
        onChange={update("message")}
      />

      <button
        type="submit"
        disabled={loading}
        data-testid="contact-submit"
        className="group inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-ink font-medium transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(201,169,97,0.4)] disabled:opacity-60"
      >
        {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} className="transition-transform group-hover:translate-x-1" />}
        {loading ? "Wird gesendet…" : "Kostenlose Besichtigung anfragen"}
      </button>
    </form>
  );
}
